import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useToast,
  type TableCellProps,
  type TableRowProps,
} from "@chakra-ui/react";
import { EmailInviteAccountTag } from "../sourcing-events/buyer/wizard/steps/private/EmailInviteAccountTag";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import {
  EmailInviteStatusDto,
  OrganizationInviteStatusDto,
  OrganizationType,
  SourcingEventStatus,
  useAddOrganizationInviteRecipientsMutation,
  useAddOrganizationInviteToPrivateSourcingEventMutation,
  useCreateEmailInviteReminderForPrivateSourcingEventMutation,
  useGetOrganizationQuery,
  useRemoveEmailInviteFromPrivateSourcingEventMutation,
  useRemoveOrganizationInviteFromPrivateSourcingEventMutation,
  useRestoreOrganizationInviteRemovedInChangeMutation,
  type EmailInviteDto,
  type MinimalOrgDto,
  type OrganizationEntryDto,
  type OrganizationInviteDto,
  type Private,
  type SourcingEventDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { displayDateWithTime } from "../../common/formatting/displayDateWithTime";
import { OrganizationSelector } from "../../common/organization/OrganizationSelector";
import { OrganizationTypeTag } from "../../common/organization/OrganizationTypeTag";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { CreateOrganizationEntryModal } from "../organizations/entry/create-entry/CreateOrganizationEntryModal";
import { EmaiInviteStatusTag } from "./EmailInviteStatusTag";
import {
  InviteOrganizationModal,
  type OrganizationInviteRecipients,
} from "../sourcing-events/buyer/wizard/steps/private/InviteOrganizationModal";
import {
  registeredMemberInvite,
  type RegisteredMember,
} from "../sourcing-events/buyer/wizard/steps/private/RegisteredAccountHint";
import { SectionCard } from "../sourcing-events/portal/view-single/SectionCard";
import {
  isBeforeBidDeadline,
  isDeadlineRefusal,
  useHasPassed,
} from "./bidDeadlineWindow";
import { publishedBidDeadline } from "./qualification/stage";
import { useChangeFlow } from "./changes/useChangeFlow";
import { emailInviteRemovalText, orgRemovalText } from "./inviteRemovalText";
import { OrgInviteStatusTag } from "./OrgInviteStatusTag";
import { useCanManageSourcingEvent } from "./useCanManageSourcingEvent";
import { InviteOrganizationEntryModal } from "./InviteOrganizationEntryModal";
import { EntryRemovalFailureAlert, useEntryRemoval } from "./useEntryRemoval";
import { OrganizationNameCell } from "../../common/organizations/OrganizationNameCell";
import { KeyboardScrollBox } from "../../common/KeyboardScrollBox";

interface Props {
  event: SourcingEventDto & { eventContext: Private };
}

export const InvitesSection = ({ event }: Props) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const toast = useToast();
  const stacked =
    useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;

  const [addOrgInviteMutation] =
    useAddOrganizationInviteToPrivateSourcingEventMutation();
  const [addRecipientsMutation] = useAddOrganizationInviteRecipientsMutation();
  const [removeOrgInviteMutation, { isLoading: isRemovingOrgInvite }] =
    useRemoveOrganizationInviteFromPrivateSourcingEventMutation();
  const [restoreOrgInvite, { isLoading: isRestoringOrgInvite }] =
    useRestoreOrganizationInviteRemovedInChangeMutation();
  const [removeEmailInvite, { isLoading: isRemovingEmailInvite }] =
    useRemoveEmailInviteFromPrivateSourcingEventMutation();
  const [sendReminder] =
    useCreateEmailInviteReminderForPrivateSourcingEventMutation();

  const [selectedOrgEntry, setSelectedOrgEntry] = useState<
    MinimalOrgDto | undefined
  >();
  const [selectedOrgForRecipients, setSelectedOrgForRecipients] = useState<
    MinimalOrgDto | undefined
  >();
  const [showCreateEntryModal, setShowCreateEntryModal] = useState(false);
  const [inviteToAddRecipientsTo, setInviteToAddRecipientsTo] = useState<
    { organization: MinimalOrgDto; invite: OrganizationInviteDto } | undefined
  >();

  const orgInvites = event.eventContext.organizationInvites;
  const emailInvites: EmailInviteDto[] = event.eventContext.emailInvites ?? [];
  const orgInvitesRemovedInChange =
    event.eventContext.organizationInvitesRemovedInChange ?? [];
  const emailInvitesRemovedInChange =
    event.eventContext.emailInvitesRemovedInChange ?? [];
  const entryGroups = groupByEntry(emailInvites, emailInvitesRemovedInChange);
  const isDraft = event.status === SourcingEventStatus.Draft;
  const isPublished = event.status === SourcingEventStatus.Published;
  const canManageInvites = useCanManageSourcingEvent(event);
  const canAddInvites = canManageInvites && (isDraft || isPublished);
  const bidDeadline = publishedBidDeadline(event);
  const change = useChangeFlow(event);
  const [refusedForDeadline, setRefusedForDeadline] = useState<
    string | null | undefined
  >(undefined);
  const invitingRefusedAfterDeadline =
    refusedForDeadline !== undefined &&
    refusedForDeadline === (bidDeadline ?? null);
  const closeInviting = () => setRefusedForDeadline(bidDeadline ?? null);
  const deadlineHasPassed = useHasPassed(isPublished ? bidDeadline : undefined);
  const canInvite =
    canAddInvites &&
    (isDraft ||
      (isBeforeBidDeadline(bidDeadline) &&
        !deadlineHasPassed &&
        !invitingRefusedAfterDeadline));
  const showStatus = !isDraft;
  const invitesGoThroughTheChange =
    canAddInvites && isPublished && !canInvite && change.draft != null;
  const changeHasPendingInvitations =
    orgInvitesRemovedInChange.length > 0 ||
    emailInvitesRemovedInChange.length > 0 ||
    emailInvites.some((invite) => invite.addedInChange) ||
    orgInvites.some(
      (invite) =>
        invite.addedInChange ||
        (invite.persons ?? []).some((person) => person.addedInChange) ||
        (invite.emailInvites ?? []).some((email) => email.addedInChange),
    );

  const invitedOrgIds = useMemo(
    () => orgInvites.map((invite) => invite.organizationId),
    [orgInvites],
  );
  const orgsToExclude = useMemo(
    () => [
      selectedOrg.id,
      ...invitedOrgIds,
      ...emailInvites
        .map((invite) => invite.organizationEntry?.id)
        .filter((id): id is string => !!id),
    ],
    [selectedOrg.id, invitedOrgIds, emailInvites],
  );

  const addOrgInvite = async (
    organizationId: string,
    recipients: OrganizationInviteRecipients,
  ) => {
    if (invitedOrgIds.includes(organizationId)) return;
    try {
      await addOrgInviteMutation({
        eventId: event.id,
        addOrganizationInviteRequest: {
          organizationId,
          persons: recipients.personIds.map((id) => ({ id })),
          emailInvites: recipients.emailInvites,
        },
      }).unwrap();
    } catch (e) {
      trigger(e);
      if (!isDeadlineRefusal(e)) throw e;
      closeInviting();
    }
  };

  const addRecipients = async (
    organizationId: string,
    recipients: OrganizationInviteRecipients,
  ) => {
    try {
      await addRecipientsMutation({
        eventId: event.id,
        pathOrganizationId: organizationId,
        addOrganizationInviteRecipientsRequest: {
          persons: recipients.personIds.map((id) => ({ id })),
          emailInvites: recipients.emailInvites,
        },
      }).unwrap();
    } catch (e) {
      trigger(e);
      if (!isDeadlineRefusal(e)) throw e;
      closeInviting();
    }
  };

  const inviteRegisteredMember = async (member: RegisteredMember) => {
    const recipients = { personIds: [member.personId], emailInvites: [] };
    const invite = registeredMemberInvite(orgInvites, member);
    if (invite === "newInvite") await addOrgInvite(member.organization.id, recipients);
    if (invite === "addToInvite") await addRecipients(member.organization.id, recipients);
  };

  const removeOrgInvite = async (organizationId: string) => {
    try {
      await removeOrgInviteMutation({
        eventId: event.id,
        pathOrganizationId: organizationId,
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const undoRemoval = async (organizationId: string) => {
    try {
      await restoreOrgInvite({
        eventId: event.id,
        pathOrganizationId: organizationId,
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const confirmRemoveOrgInvite = (organizationId: string, organizationName?: string) => {
    if (isDraft) {
      removeOrgInvite(organizationId);
      return;
    }
    confirmationModal.show({
      title: t("Confirm removal"),
      description: orgRemovalText(organizationName, change.draft != null, t),
      onConfirmed: () => removeOrgInvite(organizationId),
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const selectOrganization = (organization?: MinimalOrgDto) => {
    if (!organization) return;
    if (organization.type === OrganizationType.Organization) {
      if (!invitedOrgIds.includes(organization.id))
        setSelectedOrgForRecipients(organization);
    } else {
      setSelectedOrgEntry(organization);
    }
  };

  const confirmRemoveEmailInvite = (invite: EmailInviteDto) => {
    confirmationModal.show({
      title: t("Confirm removal"),
      description: emailInviteRemovalText(invite, change.draft != null, t),
      onConfirmed: async () => {
        try {
          await removeEmailInvite({
            eventId: event.id,
            inviteId: invite.id,
          }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const entryRemoval = useEntryRemoval((inviteId) =>
    removeEmailInvite({ eventId: event.id, inviteId }).unwrap(),
  );

  const confirmRemoveEntry = (entryKey: string, entryName: string, contacts: EmailInviteDto[]) => {
    const remove = () => entryRemoval.removeEntry(entryKey, entryName, contacts.map((invite) => invite.id));
    if (isDraft) {
      void remove();
      return;
    }
    confirmationModal.show({
      title: t("Confirm removal"),
      description: orgRemovalText(entryName, change.draft != null, t),
      onConfirmed: remove,
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const confirmSendReminder = (invite: EmailInviteDto) => {
    confirmationModal.show({
      title: t("Send email reminder?"),
      description: `${t("Are you sure you want to send an email reminder to")} ${invite.email}`,
      confirmButtonText: t("Send reminder") ?? "Send reminder",
      onConfirmed: async () => {
        try {
          await sendReminder({
            eventId: event.id,
            inviteId: invite.id,
          }).unwrap();
          toast({
            title: t("Email reminder sent!"),
            description: t("The email reminder was successfully sent"),
            status: "success",
          });
        } catch (e) {
          toast({
            title: t("Could not send reminder"),
            description: t("Something went wrong, please try again"),
            status: "error",
          });
        }
      },
    });
  };

  return (
    <SectionCard
      icon={FiUsers}
      title={t("Invited organizations")}
      count={
        orgInvites.length +
        orgInvitesRemovedInChange.length +
        entryGroups.length
      }
    >
      {confirmationModal.modal}

      {showCreateEntryModal && (
        <CreateOrganizationEntryModal
          onConfirm={async (newEntry) => {
            setSelectedOrgEntry({
              type: OrganizationType.OrganizationEntry,
              ...newEntry,
            });
          }}
          onSelectExisting={(org) => selectOrganization(org)}
          onClose={() => setShowCreateEntryModal(false)}
        />
      )}

      <InviteOrganizationEntryModal
        eventId={event.id}
        onInviteRegisteredMember={inviteRegisteredMember}
        showPublishedWarning={isPublished}
        minimalOrgEntry={selectedOrgEntry}
        alreadyInvited={emailInvites}
        onClose={() => setSelectedOrgEntry(undefined)}
        onInvitingClosed={closeInviting}
      />

      <InviteOrganizationModal
        eventId={event.id}
        minimalOrg={selectedOrgForRecipients}
        onInviteRegisteredMember={inviteRegisteredMember}
        onClose={() => setSelectedOrgForRecipients(undefined)}
        onCreate={async (recipients) => {
          if (selectedOrgForRecipients)
            await addOrgInvite(selectedOrgForRecipients.id, recipients);
        }}
        showPublishedWarning={isPublished}
      />

      <InviteOrganizationModal
        eventId={event.id}
        minimalOrg={inviteToAddRecipientsTo?.organization}
        onInviteRegisteredMember={inviteRegisteredMember}
        alreadyInvited={
          inviteToAddRecipientsTo && {
            personIds: (inviteToAddRecipientsTo.invite.persons ?? []).map(
              (person) => person.id,
            ),
            emails: [
              ...(inviteToAddRecipientsTo.invite.persons ?? [])
                .map((person) => person.email)
                .filter((email): email is string => !!email),
              ...(inviteToAddRecipientsTo.invite.emailInvites ?? []).map(
                (invite) => invite.email,
              ),
            ],
          }
        }
        confirmButtonText={t("Add people") ?? undefined}
        onClose={() => setInviteToAddRecipientsTo(undefined)}
        onCreate={async (recipients) => {
          if (inviteToAddRecipientsTo)
            await addRecipients(
              inviteToAddRecipientsTo.invite.organizationId,
              recipients,
            );
        }}
        showPublishedWarning={isPublished}
      />

      {invitesGoThroughTheChange && (
        <Flex
          pb="5"
          gap="3"
          align="center"
          wrap="wrap"
          data-testid="invite-through-change"
        >
          <Text color="smMuted" fontSize="sm">
            {t(
              "The current bid deadline has passed. Companies you add in the change are invited when the change is sent.",
            )}
          </Text>
          <Button
            size="sm"
            variant="outline"
            colorScheme="teal"
            onClick={change.openChange}
          >
            {change.actionLabel}
          </Button>
        </Flex>
      )}

      {canInvite && (
        <Box pb="5" data-testid="invite-organization-selector">
          <Text color="smMuted" fontSize="sm" pb="2">
            {t("Invite organizations to this private procurement.")}
          </Text>
          <Flex maxW={{ base: "full", md: "md" }}>
            <OrganizationSelector
              orgsToExclude={orgsToExclude}
              onChange={({ organization }) => selectOrganization(organization)}
            />
          </Flex>
          <Text fontSize="sm" color="smMuted" pt="1">
            {t("Choose a registered company, or")}{" "}
            <Button
              variant="link"
              colorScheme="teal"
              fontWeight="bold"
              fontSize="sm"
              onClick={() => setShowCreateEntryModal(true)}
            >
              {t("create a new company entry")}
            </Button>
          </Text>
        </Box>
      )}

      {changeHasPendingInvitations && (
        <Text
          color="smMuted"
          fontSize="sm"
          pb="3"
          data-testid="invitations-pending-in-change"
        >
          {t(
            "The marked invitations change when you send the change. Until then suppliers keep the list as it was.",
          )}
        </Text>
      )}

      {entryRemoval.failure && (
        <EntryRemovalFailureAlert
          failure={entryRemoval.failure}
          isRetrying={entryRemoval.removingKey === entryRemoval.failure.entryKey}
          onRetry={entryRemoval.retry}
        />
      )}
      <Box
        border="1px solid"
        borderColor="smBorder"
        rounded="md"
        overflow="hidden"
      >
        <StackedRows.Provider value={stacked}>
          <KeyboardScrollBox as={TableContainer} whiteSpace="normal">
            <Table
              variant="simple"
              size="sm"
              display={stacked ? "block" : undefined}
            >
              {!stacked && (
                <Thead>
                  <Tr>
                    <Th>{t("Organization name")}</Th>
                    <Th>{t("Type")}</Th>
                    <Th>{t("Recipients")}</Th>
                    {showStatus && <Th>{t("Status")}</Th>}
                    {canAddInvites && (
                      <Th textAlign="right" whiteSpace="nowrap">
                        {t("Actions")}
                      </Th>
                    )}
                  </Tr>
                </Thead>
              )}
              <Tbody display={stacked ? "block" : undefined}>
                {orgInvites.map((invite) => (
                  <InvitedOrgRow
                    key={invite.organizationId}
                    eventId={event.id}
                    invite={invite}
                    showStatus={showStatus}
                    showActions={canAddInvites}
                    showRemove={canAddInvites}
                    removeUnavailableReason={
                      invite.remove?.allowed === false && invite.remove.reason
                        ? t(invite.remove.reason.code)
                        : undefined
                    }
                    isRemoving={isRemovingOrgInvite}
                    onRemove={(organizationName) =>
                      confirmRemoveOrgInvite(invite.organizationId, organizationName)
                    }
                    onAddRecipients={(organization) =>
                      setInviteToAddRecipientsTo({ organization, invite })
                    }
                    showAddPeople={canInvite}
                  />
                ))}
                {orgInvitesRemovedInChange.map((invite) => (
                  <InvitedOrgRow
                    key={`removed-${invite.organizationId}`}
                    eventId={event.id}
                    invite={invite}
                    removedInChange
                    removeUnavailableReason={
                      invite.remove?.allowed === false && invite.remove.reason
                        ? t(invite.remove.reason.code)
                        : undefined
                    }
                    showStatus={showStatus}
                    showActions={canAddInvites}
                    showRemove={false}
                    isRemoving={isRestoringOrgInvite}
                    onRemove={() => undefined}
                    onUndoRemoval={() =>
                      void undoRemoval(invite.organizationId)
                    }
                    onAddRecipients={() => undefined}
                    showAddPeople={false}
                  />
                ))}
                {entryGroups.map((group) => (
                  <EntryInviteRow
                    key={group.key}
                    group={group}
                    showStatus={showStatus}
                    showActions={canAddInvites}
                    showAddPeople={canInvite}
                    showReminder={isPublished}
                    isRemoving={isRemovingEmailInvite}
                    onAddPeople={(entry) =>
                      setSelectedOrgEntry(asMinimalEntry(entry))
                    }
                    onSendReminder={confirmSendReminder}
                    onRemove={confirmRemoveEmailInvite}
                    isRemovingEntry={entryRemoval.removingKey === group.key}
                    onRemoveEntry={(entryName, contacts) => confirmRemoveEntry(group.key, entryName, contacts)}
                  />
                ))}
                {orgInvites.length +
                  orgInvitesRemovedInChange.length +
                  entryGroups.length ===
                  0 && (
                  <Row>
                    <Cell
                      colSpan={
                        3 + (showStatus ? 1 : 0) + (canAddInvites ? 1 : 0)
                      }
                      textAlign="center"
                      color="smMuted"
                    >
                      {t("No invites have been added")}
                    </Cell>
                  </Row>
                )}
              </Tbody>
            </Table>
          </KeyboardScrollBox>
        </StackedRows.Provider>
      </Box>
    </SectionCard>
  );
};

const StackedRows = createContext(false);

const Row = (props: TableRowProps) => {
  const stacked = useContext(StackedRows);
  return (
    <Tr
      {...props}
      {...(stacked
        ? {
            display: "block",
            borderBottom: "1px solid",
            borderColor: "smBorder",
            py: "2",
          }
        : {})}
    />
  );
};

const Cell = (props: TableCellProps) => {
  const stacked = useContext(StackedRows);
  return (
    <Td
      verticalAlign="top"
      {...props}
      {...(stacked
        ? {
            display: "block",
            borderBottom: "none",
            textAlign: "left",
            whiteSpace: "normal",
            py: "1",
          }
        : {})}
    />
  );
};

const InChangeTag = ({ kind }: { kind: "added" | "removed" }) => {
  const { t } = useTranslation();
  return (
    <Tag
      size="sm"
      colorScheme={kind === "added" ? "teal" : "red"}
      variant="subtle"
      mt="1"
    >
      {kind === "added"
        ? t("Invited when the change is sent")
        : t("Removed when the change is sent")}
    </Tag>
  );
};

const InvitedOrgRow = ({
  eventId,
  invite,
  removedInChange = false,
  onUndoRemoval,
  showStatus,
  showActions,
  showRemove,
  removeUnavailableReason,
  isRemoving,
  onRemove,
  onAddRecipients,
  showAddPeople,
}: {
  eventId: string;
  invite: OrganizationInviteDto;
  removedInChange?: boolean;
  onUndoRemoval?: () => void;
  showStatus: boolean;
  showActions: boolean;
  showRemove: boolean;
  removeUnavailableReason?: ReactNode;
  isRemoving: boolean;
  onRemove: (organizationName?: string) => void;
  onAddRecipients: (organization: MinimalOrgDto) => void;
  showAddPeople: boolean;
}) => {
  const { t } = useTranslation();
  const orgQuery = useGetOrganizationQuery({
    orgId: invite.organizationId,
  });
  const org = orgQuery.data;
  const stacked = useContext(StackedRows);

  return (
    <Row>
      <Cell>
        <OrganizationNameCell query={orgQuery} />
        {invite.addedInChange && <InChangeTag kind="added" />}
        {removedInChange && <InChangeTag kind="removed" />}
        {removedInChange && removeUnavailableReason && (
          <Text
            fontSize="xs"
            color="smError"
            pt="1"
            data-testid="removal-blocks-send"
          >
            {removeUnavailableReason}{" "}
            {t("Undo the removal before you send the change.")}
          </Text>
        )}
      </Cell>
      <Cell>
        <OrganizationTypeTag type={OrganizationType.Organization} />
      </Cell>
      <Cell>
        {(invite.persons ?? []).map((person) => (
          <Text key={person.id} fontSize="sm" overflowWrap="anywhere">
            {`${person.firstName} ${person.lastName} (${person.email})`.trim()}
            {person.addedInChange && (
              <>
                {" "}
                <InChangeTag kind="added" />
              </>
            )}
          </Text>
        ))}
        {(invite.emailInvites ?? []).map((email) => (
          <Text key={email.email} fontSize="sm" pt="1" overflowWrap="anywhere">
            {email.email} <EmailInviteAccountTag eventId={eventId} email={email.email} canLookUp={showActions} />
            {email.addedInChange && (
              <>
                {" "}
                <InChangeTag kind="added" />
              </>
            )}
          </Text>
        ))}
        {!invite.persons?.length && !invite.emailInvites?.length && (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Cell>
      {showStatus && (
        <Cell>
          {invite.addedInChange ? (
            <Text fontSize="sm" color="smMuted">
              –
            </Text>
          ) : (
            <OrgInviteStatusTag
              status={invite.status ?? OrganizationInviteStatusDto.Invited}
            />
          )}
        </Cell>
      )}
      {showActions && (
        <Cell textAlign="right" whiteSpace="nowrap">
          <Flex
            justify={stacked ? "flex-start" : "flex-end"}
            align="center"
            gap="2"
          >
            {showAddPeople && org && (
              <Button
                variant="solid"
                size={stacked ? "sm" : "xs"}
                colorScheme="teal"
                onClick={() =>
                  onAddRecipients({
                    type: OrganizationType.Organization,
                    id: org.id,
                    name: org.name,
                    organizationNumber: org.organizationNumber,
                    country: org.country,
                  })
                }
              >
                {t("Add people")}
              </Button>
            )}
            {removedInChange && onUndoRemoval && (
              <Button
                variant="outline"
                size={stacked ? "sm" : "xs"}
                colorScheme="teal"
                isDisabled={isRemoving}
                onClick={onUndoRemoval}
              >
                {t("Undo removal")}
              </Button>
            )}
            {showRemove && (
              <Explanation
                enabled={!!removeUnavailableReason}
                text={removeUnavailableReason}
                shouldWrapChildren
              >
                <IconButton
                  aria-label={t("Remove") ?? "Remove"}
                  icon={<Icon as={FaTimes} />}
                  size="sm"
                  variant="ghost"
                  isDisabled={isRemoving || !!removeUnavailableReason}
                  onClick={() => onRemove(org?.name)}
                />
              </Explanation>
            )}
          </Flex>
        </Cell>
      )}
    </Row>
  );
};

interface EntryContact {
  invite: EmailInviteDto;
  removedInChange: boolean;
}

interface EntryGroup {
  key: string;
  entry: OrganizationEntryDto | undefined;
  contacts: EntryContact[];
}

const groupByEntry = (
  invites: EmailInviteDto[],
  removedInChange: EmailInviteDto[],
): EntryGroup[] => {
  const groups = new Map<string, EntryGroup>();
  const contacts: EntryContact[] = [
    ...invites.map((invite) => ({ invite, removedInChange: false })),
    ...removedInChange.map((invite) => ({ invite, removedInChange: true })),
  ];
  contacts.forEach((contact) => {
    const entry = contact.invite.organizationEntry ?? undefined;
    const key = entry ? `entry-${entry.id}` : `invite-${contact.invite.id}`;
    const group = groups.get(key) ?? { key, entry, contacts: [] };
    group.contacts.push(contact);
    groups.set(key, group);
  });
  return [...groups.values()];
};

const EntryInviteRow = ({
  group,
  showStatus,
  showActions,
  showAddPeople,
  showReminder,
  isRemoving,
  onAddPeople,
  onSendReminder,
  onRemove,
  isRemovingEntry,
  onRemoveEntry,
}: {
  group: EntryGroup;
  showStatus: boolean;
  showActions: boolean;
  showAddPeople: boolean;
  showReminder: boolean;
  isRemoving: boolean;
  onAddPeople: (entry: OrganizationEntryDto) => void;
  onSendReminder: (invite: EmailInviteDto) => void;
  onRemove: (invite: EmailInviteDto) => void;
  isRemovingEntry: boolean;
  onRemoveEntry: (entryName: string, contacts: EmailInviteDto[]) => void;
}) => {
  const { t } = useTranslation();
  const { entry, contacts } = group;
  const liveContacts = contacts.filter(({ removedInChange }) => !removedInChange).map(({ invite }) => invite);
  const stacked = useContext(StackedRows);
  const wholeEntryInChange = (kind: "added" | "removed") =>
    contacts.every(({ invite, removedInChange }) =>
      kind === "added" ? invite.addedInChange : removedInChange,
    );

  return (
    <Row>
      <Cell>
        <Text fontSize="sm">{entry?.name ?? t("No organization")}</Text>
        {wholeEntryInChange("added") && <InChangeTag kind="added" />}
        {wholeEntryInChange("removed") && <InChangeTag kind="removed" />}
      </Cell>
      <Cell>
        <OrganizationTypeTag type={OrganizationType.OrganizationEntry} />
      </Cell>
      <Cell colSpan={showStatus ? 2 : 1}>
        <Stack spacing="2">
          {contacts.map(({ invite, removedInChange }) => (
            <Flex
              key={invite.id}
              align="flex-start"
              justify={stacked ? "space-between" : "flex-start"}
              gap="3"
              wrap="wrap"
            >
              <Box minW="0">
                <Text fontSize="sm" overflowWrap="anywhere">
                  {invite.email}
                  {invite.addedInChange && !wholeEntryInChange("added") && (
                    <>
                      {" "}
                      <InChangeTag kind="added" />
                    </>
                  )}
                  {removedInChange && !wholeEntryInChange("removed") && (
                    <>
                      {" "}
                      <InChangeTag kind="removed" />
                    </>
                  )}
                </Text>
                {showStatus && (
                  <Flex direction="column" align="flex-start" gap="1" pt="1">
                    <EmaiInviteStatusTag
                      status={invite.status}
                      openedAt={invite.openedAt ?? undefined}
                      size="sm"
                    />
                    {invite.lastReminderSentAt && (
                      <Text fontSize="xs" fontStyle="italic">
                        {t("Last reminder sent")}{" "}
                        {displayDateWithTime(
                          invite.lastReminderSentAt,
                          t("at"),
                        )}
                      </Text>
                    )}
                  </Flex>
                )}
              </Box>
              {showActions && !removedInChange && (
                <Flex align="center" gap="2">
                  {showReminder &&
                    invite.status !== EmailInviteStatusDto.SendingFailed &&
                    invite.status !== EmailInviteStatusDto.Pending && (
                      <Button
                        variant="solid"
                        size={stacked ? "sm" : "xs"}
                        colorScheme="orange"
                        onClick={() => onSendReminder(invite)}
                      >
                        {t("Send reminder")}
                      </Button>
                    )}
                  <IconButton
                    aria-label={`${t("Remove") ?? "Remove"} ${invite.email}`}
                    icon={<Icon as={FaTimes} />}
                    size="sm"
                    variant="ghost"
                    isDisabled={isRemoving || isRemovingEntry}
                    onClick={() => onRemove(invite)}
                  />
                </Flex>
              )}
            </Flex>
          ))}
        </Stack>
      </Cell>
      {showActions && (
        <Cell textAlign="right" whiteSpace="nowrap">
          {showAddPeople && entry && !wholeEntryInChange("removed") && (
            <Button
              variant="solid"
              size={stacked ? "sm" : "xs"}
              colorScheme="teal"
              onClick={() => onAddPeople(entry)}
            >
              {t("Add people")}
            </Button>
          )}
          {entry && liveContacts.length > 0 && (
            <IconButton
              aria-label={t("Remove {{organization}}", { organization: entry.name })}
              icon={<Icon as={FaTimes} />}
              size="sm"
              variant="ghost"
              ml="2"
              isLoading={isRemovingEntry}
              isDisabled={isRemoving || isRemovingEntry}
              onClick={() => onRemoveEntry(entry.name, liveContacts)}
            />
          )}
        </Cell>
      )}
    </Row>
  );
};

const asMinimalEntry = (entry: OrganizationEntryDto): MinimalOrgDto => ({
  type: OrganizationType.OrganizationEntry,
  id: entry.id,
  name: entry.name,
  organizationNumber: entry.organizationNumber,
  country: entry.country,
});
